import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import styled from 'styled-components';
import { Plus, RefreshCw, Trash2 } from 'lucide-react';
import CurrencyInput from '../../../../../components/CurrencyInput';

import { OrderSchemaType } from '../order.schema';
import { useSteelInstallments } from '../Hooks/useSteelInstallments';
import {
  SubTitle,
  Input,
  Button,
  Separator,
  SwitchBox,
  SwitchInput,
  SwitchSlider,
} from '../style';
import { MismatchAlert } from './PaymentSection.style';

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const InstallmentsTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InstallmentRow = styled.div`
  display: grid;
  grid-template-columns: 48px 1fr 1fr 1.4fr 48px;
  gap: 16px;
  align-items: center;
`;

const HeaderRow = styled(InstallmentRow)`
  & > span {
    font-family: 'Inter', sans-serif;
    font-weight: 700;
    color: #303030;
  }
`;

const NumberCell = styled.span`
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  color: #696969;
  text-align: center;
`;

const ComissaoCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #F87171;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;

  &:hover {
    color: #DC2626;
  }
`;

const EmptyMessage = styled.p`
  font-family: 'Inter', sans-serif;
  color: #696969;
  font-size: 14px;
`;

const SummaryLine = styled.div<{ mismatch?: boolean }>`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: ${props => (props.mismatch ? '#F87171' : '#303030')};
  font-weight: 700;
`;

const toCurrency = (value: number) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

interface PaymentSectionProps {
  isEditMode?: boolean;
  loadedConditionId?: number;
}

const PaymentSection: React.FC<PaymentSectionProps> = ({ isEditMode = false, loadedConditionId }) => {
  const form = useFormContext<OrderSchemaType>();
  const { control, register, watch } = form;
  const installmentsApi = useSteelInstallments({ form, isEditMode, loadedConditionId });
  const {
    fields,
    remove,
    handleAddInstallment,
    handleRefreshDueDates,
    sumInstallments,
    total,
    hasMismatch,
  } = installmentsApi;

  const handleAdjustLast = () => {
    const values = form.getValues('installments') ?? [];
    if (values.length === 0) return;
    const last = values.length - 1;
    const others = values.slice(0, last).reduce((acc, p) => acc + (Number(p?.valor) || 0), 0);
    const rest = Math.round((total - others) * 100) / 100;
    form.setValue(`installments.${last}.valor`, rest > 0 ? rest : 0, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <>
      <SectionHeader>
        <SubTitle>Parcelas</SubTitle>
        <Button type="button" variant="outline" onClick={handleRefreshDueDates}>
          <RefreshCw size={16} />
          Atualizar data
        </Button>
      </SectionHeader>

      {fields.length === 0 && (
        <EmptyMessage>
          Selecione uma condição de pagamento ou adicione parcelas manualmente.
        </EmptyMessage>
      )}

      {fields.length > 0 && (
        <InstallmentsTable>
          <HeaderRow>
            <span style={{ textAlign: 'center' }}>Nº</span>
            <span>Valor</span>
            <span>Vencimento</span>
            <span>Comissão</span>
            <span />
          </HeaderRow>

          {fields.map((field, index) => {
            const comissaoAtiva = watch(`installments.${index}.comissaoAtiva`);
            return (
              <InstallmentRow key={field.id}>
                <NumberCell>{index + 1}</NumberCell>

                <Controller
                  name={`installments.${index}.valor`}
                  control={control}
                  render={({ field: valorField }) => (
                    <CurrencyInput
                      id={`installment-valor-${index}`}
                      name={valorField.name}
                      value={Number(valorField.value) || 0}
                      onValueChange={(value, name, values) => {
                        valorField.onChange(values?.float ?? 0);
                      }}
                      placeholder="R$ 0,00"
                    />
                  )}
                />

                <Input
                  type="date"
                  {...register(`installments.${index}.vencimento`)}
                />

                <ComissaoCell>
                  <Controller
                    name={`installments.${index}.comissaoAtiva`}
                    control={control}
                    render={({ field: comissaoField }) => (
                      <SwitchBox>
                        <SwitchInput
                          type="checkbox"
                          checked={comissaoField.value || false}
                          onChange={(e) => comissaoField.onChange(e.target.checked)}
                        />
                        <SwitchSlider className="slider" />
                      </SwitchBox>
                    )}
                  />
                  {comissaoAtiva && (
                    <Controller
                      name={`installments.${index}.comissaoPercentual`}
                      control={control}
                      render={({ field: percentualField }) => (
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="%"
                          style={{ maxWidth: 90 }}
                          value={percentualField.value ?? ''}
                          onChange={(e) =>
                            percentualField.onChange(
                              e.target.value === '' ? undefined : Number(e.target.value),
                            )
                          }
                        />
                      )}
                    />
                  )}
                </ComissaoCell>

                <RemoveButton type="button" onClick={() => remove(index)} title="Remover parcela">
                  <Trash2 size={18} />
                </RemoveButton>
              </InstallmentRow>
            );
          })}
        </InstallmentsTable>
      )}

      <Separator />

      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Button type="button" variant="outline" onClick={handleAddInstallment}>
          <Plus size={16} />
          Adicionar parcela
        </Button>
      </div>

      {hasMismatch && (
        <MismatchAlert role="alert" id="payment-mismatch-alert">
          <span>
            As parcelas somam {toCurrency(sumInstallments)} e o total do pedido é {toCurrency(total)} (diferença{' '}
            {toCurrency(Math.abs(total - sumInstallments))}). Ajuste as parcelas para salvar.
          </span>
          <Button type="button" variant="outline" onClick={handleAdjustLast}>
            Ajustar parcelas
          </Button>
        </MismatchAlert>
      )}

      {fields.length > 0 && (
        <SummaryLine mismatch={hasMismatch}>
          <span>Soma das parcelas: {toCurrency(sumInstallments)}</span>
          <span>·</span>
          <span>Total: {toCurrency(total)}</span>
        </SummaryLine>
      )}
    </>
  );
};

export default PaymentSection;
